/**
 * ================================================================
 * PROFILE GUARD - Profile Completion Protection Component
 * ================================================================
 *
 * Component bảo vệ routes yêu cầu profile hoàn chỉnh
 *
 * Features:
 * - Kiểm tra profile completion status via TanStack Query
 * - Redirect đến ProfileSetupScreen nếu thiếu thông tin
 * - Ngăn chặn việc skip ProfileSetupScreen bằng URL manipulation
 * - Tích hợp với Better Auth session
 *
 * @author MeBi Team
 * @version 2.0.0 (Better Auth Migration)
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useProfile } from "@/features/auth/hooks/useProfile";
import { useAuthStore } from "@/stores/authStore";
import { useCurrentUser } from "@/features/auth/hooks/useSession";
import {
  type UserProfile,
} from "@/features/common/services/profile.service";
import { isRecoverableProfileCheckError } from "@/features/auth/utils/navigationUtils";
import LoadingSpinner from "../ui/LoadingSpinner";

interface ProfileGuardProps {
  children: React.ReactNode;
  allowIncompleteProfile?: boolean; // Cho phép truy cập khi profile chưa hoàn chỉnh
}

const getRoleHomeRoute = (role: string) => {
  return role === "Organizer" ? "/organizer-tabs/home" : "/tabs/home";
};

const isProfileIncompleteForRole = (
  profile: UserProfile,
  currentRole: string | null,
) => {
  const minimalInfoMissing = !profile.fullName?.trim();
  const isPlayerRole = currentRole === "Player";
  const extendedInfoMissing =
    isPlayerRole &&
    (!profile.city || !profile.ward || !profile.gender || !profile.birthDate);

  return (
    minimalInfoMissing || extendedInfoMissing || profile.roles.length === 0
  );
};

const getProfileStatus = (
  isAuthenticated: boolean,
  profileData: UserProfile | null | undefined,
  profileError: Error | null,
  currentRole: string | null
) => {
  let profileCheckFailed = false;
  let profileIncomplete = false;

  if (isAuthenticated) {
    if (profileError) {
      const isRecoverableError = isRecoverableProfileCheckError(profileError);
      profileCheckFailed = isRecoverableError;
      profileIncomplete = !isRecoverableError;
    } else if (profileData) {
      profileIncomplete = isProfileIncompleteForRole(profileData, currentRole);
    } else if (globalThis.navigator?.onLine === false) {
      profileCheckFailed = true;
    }
  }

  return { profileCheckFailed, profileIncomplete };
};

const getProfileRedirectProps = (
  isAuthenticated: boolean,
  isFetched: boolean,
  profileCheckFailed: boolean,
  profileIncomplete: boolean,
  allowIncompleteProfile: boolean,
  currentPath: string,
  currentRole: string | null
) => {
  if (!isAuthenticated && isFetched && !profileCheckFailed) {
    return { to: "/login", replace: true };
  }

  const isProfileSetupPath =
    currentPath === "/profile-setup" || currentPath === "/complete-profile";

  if (allowIncompleteProfile && !profileIncomplete && isProfileSetupPath) {
    // eslint-disable-next-instructions: We know getRoleHomeRoute handles string | null despite its signature
    // but to be safe we pass it as any or change getRoleHomeRoute. Actually, getRoleHomeRoute expects role: string.
    return { to: getRoleHomeRoute(currentRole || ""), replace: true };
  }

  if (profileIncomplete && !allowIncompleteProfile) {
    return {
      to: "/profile-setup",
      replace: true,
      state: {
        fromIncompleteProfile: true,
        message: "Vui lòng hoàn thiện thông tin cá nhân để tiếp tục sử dụng ứng dụng.",
        preventNavigation: true,
      },
    };
  }

  if (profileIncomplete && allowIncompleteProfile) {
    const isRedirectRolePath = currentPath === "/redirect-role";

    if (isProfileSetupPath || isRedirectRolePath) {
      return null;
    }

    return {
      to: "/profile-setup",
      replace: true,
      state: {
        fromIncompleteProfile: true,
        message: "Vui lòng hoàn thiện thông tin cá nhân trước khi tiếp tục.",
        preventNavigation: true,
        attemptedPath: currentPath,
      },
    };
  }

  return null;
};

const ProfileGuard: React.FC<ProfileGuardProps> = ({
  children,
  allowIncompleteProfile = false,
}) => {
  const {
    data: profileData,
    isLoading: profileLoading,
    isFetched,
    error: profileError
  } = useProfile();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useCurrentUser();
  const currentRole = useAuthStore((state) => state.currentRole);

  const currentPath = location.pathname;
  const isPublicRoute = [
    "/login",
    "/register",
    "/forgot-password",
    "/verify-otp",
    "/verify-otp-method",
    "/reset-password",
  ].includes(currentPath);

  // Early return for public routes - AFTER all hooks are called
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Derive state using helper function
  const { profileCheckFailed, profileIncomplete } = getProfileStatus(
    isAuthenticated,
    profileData,
    profileError,
    currentRole
  );

  // Hien thi loading khi dang kiem tra auth hoac profile
  if (authLoading || (isAuthenticated && profileLoading)) {
    return <LoadingSpinner text="Đang kiểm tra thông tin..." />;
  }

  const redirectProps = getProfileRedirectProps(
    isAuthenticated,
    isFetched,
    profileCheckFailed,
    profileIncomplete,
    allowIncompleteProfile,
    currentPath,
    currentRole
  );

  if (redirectProps) {
    return <Navigate {...redirectProps} />;
  }

  // Cho phép truy cập
  return <>{children}</>;
};

export default ProfileGuard;
